import { useRequest } from "ahooks";
import { useEffect, useState } from "react";
import { ScrollShadow } from "@heroui/react";
import { motion } from "motion/react";
import { DrawerWrapper } from "@/comps/DrawerWrapper";
import { createNiceModal } from "@/utils/nicemodel";
import { type CountryCodeValue } from "@/main";
import { type AddressItem, fetchGeoData } from "$/utils/fetchGeoData";
import { sortByFirstChar } from "./utils";
import { ListWrapper } from "./ListWrapper";
import { Selector } from "./Selector";

export const LabelOptions = ["请选择省份", "请选择城市", "请选择区域"];

export interface GeoInfo {
  id: string;
  name: string;
}

export const openRegionCascaderModal = createNiceModal<
  {
    residenceCountry: CountryCodeValue;
    code: string;
    title: string;
    value?: AddressItem[];
  },
  { geoInfos: AddressItem[] }
>(({ _modal, residenceCountry, code, title, value }) => {
  const [activeLevel, setActiveLevel] = useState(0);
  const [geoInfos, setGeoInfos] = useState<AddressItem[]>(value ?? []);
  const {
    run: runOne,
    data: levelOneList,
    loading: loadingOne,
    error: errorOne,
  } = useRequest(
    async () => {
      const res = await fetchGeoData(code);
      return sortByFirstChar(res.children, residenceCountry);
    },
    {
      refreshDeps: [code],
    },
  );

  // 每次打开时，根据value设置初始值, 关闭的时候清理掉内部维护的地址信息
  useEffect(() => {
    if (_modal.props.isOpen) {
      setGeoInfos(value ?? []);
      setActiveLevel(value?.length ? value.length - 1 : 0);
    } else {
      setGeoInfos([]);
    }
  }, [_modal.props.isOpen]);

  const {
    run: runTwo,
    data: levelTwoList,
    loading: loadingTwo,
    error: errorTwo,
  } = useRequest(
    async () => {
      if (!geoInfos[0]?.id) {
        return [];
      }
      const res = await fetchGeoData(code, geoInfos[0].id);
      return sortByFirstChar(res.children, residenceCountry);
    },
    {
      refreshDeps: [geoInfos[0]?.id, code],
    },
  );

  const {
    run: runThree,
    data: levelThreeList,
    loading: loadingThree,
    error: errorThree,
  } = useRequest(
    async () => {
      if (!geoInfos[1]?.id) {
        return [];
      }
      const res = await fetchGeoData(code, geoInfos[1].id);
      return sortByFirstChar(res.children, residenceCountry);
    },
    {
      refreshDeps: [geoInfos[1]?.id, code],
    },
  );

  const reset = () => {
    setActiveLevel(0);
  };

  return (
    <DrawerWrapper
      baseClassName="h-[90vh] h-[90dvh]"
      {..._modal.props}
      title={title}
      onClose={reset}
    >
      <div className="flex flex-1 flex-col overflow-hidden">
        <Selector value={geoInfos} onClick={setActiveLevel} />
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden pb-9 pt-2">
          {LabelOptions.map((v, index) => {
            let loading;
            let data;
            let error;
            let run;
            switch (index) {
              case 0:
                loading = loadingOne;
                data = levelOneList;
                error = errorOne;
                run = runOne;
                break;
              case 1:
                loading = loadingTwo;
                data = levelTwoList;
                error = errorTwo;
                run = runTwo;
                break;
              case 2:
                loading = loadingThree;
                data = levelThreeList;
                error = errorThree;
                run = runThree;
                break;
              default:
                loading = false;
                data = [];
            }
            return (
              <motion.div
                key={v}
                variants={{
                  active: {
                    opacity: 1,
                    scale: 1,
                    pointerEvents: "auto",
                    transition: { duration: 0.3, ease: "easeOut" },
                  },
                  inactive: {
                    opacity: 0,
                    scale: 0.95,
                    pointerEvents: "none",
                    transition: { duration: 0.2, ease: "easeIn" },
                  },
                }}
                initial={false}
                animate={activeLevel === index ? "active" : "inactive"}
                className="absolute inset-0"
              >
                <ScrollShadow
                  className="flex h-full flex-col overflow-y-auto pb-9 pt-2"
                  key={v}
                >
                  <ListWrapper
                    value={geoInfos[index]}
                    loading={loading}
                    data={data}
                    error={error}
                    onReload={run}
                    onChange={(item) => {
                      // 重复选择不触发事件
                      if (item.id !== geoInfos[index]?.id) {
                        setGeoInfos((prev) => {
                          const copy = prev.slice();
                          copy[index] = item;
                          copy.length = index + 1;
                          return copy;
                        });
                      }

                      if (index === LabelOptions.length - 1) {
                        const newValue = geoInfos.slice();
                        newValue[index] = item;
                        _modal.resolve({ geoInfos: newValue });
                      } else {
                        setActiveLevel(index + 1);
                      }
                    }}
                  />
                </ScrollShadow>
              </motion.div>
            );
          })}
        </div>
      </div>
    </DrawerWrapper>
  );
});
